<template>
  <section class="itinerary-section-item">
    <header>
      <div class="itinerary-section-item__header-common">
        <h3 class="itinerary-section-item__title">
          <AppRouteLink :class="{ 'itinerary-section-item__link': routeTo }" fallback-tag="span" :route-to="routeTo">
            {{ item.entity.title }}
          </AppRouteLink>
        </h3>
        <ItinerarySectionItemStatuses v-if="showStatuses" :statuses="itemStatuses" />
      </div>
    </header>

    <figure v-if="!isDailyEvent || mediaUrl" class="itinerary-section-item__image-section">
      <AppRouteLink :route-to="routeTo">
        <img
          v-if="mediaUrl"
          class="itinerary-section-item__image"
          loading="lazy"
          :src="mediaUrl"
          :alt="item.entity.title"
        />
        <div v-else class="itinerary-section-item__image no-image" />
      </AppRouteLink>

      <ItinerarySectionItemActions
        v-if="!isDailyEvent"
        :can-be-favorite="item.entity.canBeFavorite"
        :can-detach="!isDaily"
        :changing-favorite="changingFavorite"
        :is-detaching="isDetaching"
        :is-favorite="isFavorite"
        @changing-favorite="changeFavoriteStatus"
        @detaching-event="handleEventDetaching"
      />
    </figure>

    <AppCollapsibleContentSection v-if="item.entity.description">
      <div class="itinerary-section-item__description" v-html="item.entity.description" />
    </AppCollapsibleContentSection>

    <div class="itinerary-section-item__details-wrapper">
      <ItinerarySectionItemDetails v-if="showDetails" :entity="item.entity" />
      <ItinerarySectionItemNotes
        v-model="notes"
        class="itinerary-section-item__notes"
        :changing="notesChanging"
        @change="changeNotes"
      />
    </div>
  </section>
</template>

<script>
import { getEntityRoute } from '@/helpers/entityType';
import ImageHandler from '@/helpers/ImageHandler';

import addToUserFavoritesMutation from '@/graphql/me/favorite/AddToUserFavorites.mutation.gql';
import deleteFromUserFavoritesMutation from '@/graphql/me/favorite/DeleteFromUserFavorites.mutation.gql';
import updateNotesInItinerarySectionItemMutation from '@/graphql/itinerary/UpdateNotesInItinerarySectionItem.mutation.gql';

import AppRouteLink from '@/components/partials/AppRouteLink.vue';
import ItinerarySectionItemNotes from '@/components/itinerary/ItinerarySectionItemNotes.vue';

export default {
  components: {
    AppRouteLink,
    ItinerarySectionItemNotes,
    ItinerarySectionItemStatuses: () => import('@/components/itinerary/ItinerarySectionItemStatuses.vue'),
    ItinerarySectionItemActions: () => import('@/components/itinerary/ItinerarySectionItemActions.vue'),
    AppCollapsibleContentSection: () => import('@/components/AppCollapsibleContentSection.vue'),
    ItinerarySectionItemDetails: () => import('@/components/itinerary/ItinerarySectionItemDetails.vue'),
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isDaily: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      changingFavorite: false,
      isFavorite: this.item.entity.isFavoriteByCurrentUser,
      notes: this.item.notes,
      notesChanging: false,
      needAllDesc: false,
    };
  },

  computed: {
    isDailyEvent() {
      return this.item.entity.graphqlType === 'ItineraryDailyEvent';
    },
    isDetaching() {
      return (
        this.$store.state.itineraryPage.isActiveItemDetaching &&
        this.$store.state.itineraryPage.itemDetachingId === this.item.id
      );
    },
    itemStatuses() {
      const { accessType, activityStatus } = this.item.entity;

      return { accessType, activityStatus };
    },
    mediaUrl() {
      return new ImageHandler().getFirstImgFromMedia(this.item.entity);
    },
    routeTo() {
      return this.item.entity.isPublished ? getEntityRoute(this.item.entity) : null;
    },
    showDetails() {
      return this.isDatesPresent || this.isTimeHoursPresent || this.item.entity.address;
    },
    isDatesPresent() {
      return this.item.entity.earliestStartDate;
    },
    isTimeHoursPresent() {
      return this.isDatesPresent || (this.item.entity.openingHourSeasons && this.item.entity.openingHourSeasons.length);
    },
    showStatuses() {
      return this.item.entity.accessType || this.item.entity.activityStatus;
    },
  },

  methods: {
    changeFavoriteStatus() {
      if (this.changingFavorite) {
        return;
      }

      this.changingFavorite = true;

      if (!this.isFavorite) {
        this.$apollo
          .mutate({
            mutation: addToUserFavoritesMutation,
            variables: {
              entities: [{ type: this.item.entity.graphqlType, id: this.item.entity.id }],
            },
          })
          .then(({ data }) => {
            this.changingFavorite = false;
            this.isFavorite = data.addToUserFavorites;
            this.$emit('favorite-changed');
          })
          .catch(() => {
            this.changingFavorite = false;
          });
        return;
      }

      this.$apollo
        .mutate({
          mutation: deleteFromUserFavoritesMutation,
          variables: {
            entities: [{ type: this.item.entity.graphqlType, id: this.item.entity.id }],
          },
        })
        .then(({ data }) => {
          this.changingFavorite = false;
          this.isFavorite = !data.deleteFromUserFavorites;
          this.$emit('favorite-changed');
        })
        .catch(() => {
          this.changingFavorite = false;
        });
    },
    changeNotes() {
      if (this.notes === this.item.notes) {
        return;
      }

      this.notesChanging = true;
      this.$apollo
        .mutate({
          mutation: updateNotesInItinerarySectionItemMutation,
          variables: {
            id: this.item.id,
            notes: this.notes,
          },
        })
        .then(({ data }) => {
          this.notesChanging = false;
          this.notes = data.updateNotesInItinerarySectionItem.notes;
          this.$emit('notes-changed');
        })
        .catch(() => {
          this.notesChanging = false;
          this.notes = this.item.notes;
        });
    },
    handleEventDetaching() {
      this.$store.dispatch('itineraryPage/setItemDetaching', { id: this.item.id });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.itinerary-section-item {
  &__event-type-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__event-type {
    padding: 6px 16px;
    font-size: 12px;
    text-align: center;
    border: 1px solid vars.$c-gray-da;
    background-color: vars.$c-total-white;

    @media (min-width: vars.$mobile-break-point + 1px) {
      padding: 8px 32px;
      font-size: 16px;
    }
  }

  &__header-common {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 12px 0;

    @media (min-width: vars.$mobile-break-point + 1px) {
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.15em;
    line-height: normal;
    text-transform: uppercase;

    @media (min-width: vars.$mobile-break-point + 1px) {
      max-width: 600px;
      font-size: 18px;
    }
  }

  &__link {
    color: vars.$c-dark-gray;

    &:hover {
      opacity: 0.7;
    }
  }

  &__image-section {
    position: relative;
    margin-bottom: 16px;

    @media (min-width: vars.$mobile-break-point + 1px) {
      margin-bottom: 24px;
    }
  }

  &__image {
    max-width: 100%;
    width: 100%;
    height: 180px;
    object-fit: cover;

    @media (min-width: vars.$mobile-break-point + 1px) {
      height: auto;
      max-height: 400px;
    }

    &.no-image {
      background-color: vars.$c-gray-d8;
    }
  }

  &__position {
    position: absolute;
    bottom: 12px;
    right: 12px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 500;
    color: vars.$c-total-white;
    background-color: vars.$c-dark-gray;
    border-radius: 100%;

    @media (min-width: vars.$mobile-break-point + 1px) {
      bottom: 16px;
      right: 16px;
      width: 32px;
      height: 32px;
      font-size: 14px;
      font-weight: 600;
    }
  }

  &__description {
    font-size: 14px;
    text-align: justify;
    line-height: 1.5;

    @media (min-width: vars.$mobile-break-point + 1px) {
      font-size: 16px;
    }

    ::v-deep {
      b,
      strong {
        font-weight: 600;
      }
      a {
        color: inherit;
        text-decoration: underline;
      }
    }
  }

  &__details-wrapper {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 16px;

    > * {
      flex: 1;
    }

    @media (min-width: vars.$media-599) {
      flex-direction: row;
      justify-content: space-between;
      gap: 48px;
      margin-top: 24px;
    }
  }

  &__notes {
    @media (min-width: vars.$media-599) {
      align-self: flex-start;
    }
  }
}
</style>
